import { useMemo } from "react";
import { DeviceType } from "@/services/ContentsService";
import { useProducts } from "@/services/product/useProductService";
import { usePricedFilter } from "@/hooks/usePricedFilter";
import { useValidBundles } from "@/hooks/useValidBundles";
import DeviceUtil from "@/utils/DeviceUtil";
import { GAME_SUPPORTED_DEVICE_SWAPS } from "@/constants/game-supported-device-swaps";

/**
 * 게임 상세 「지원 기기」 상품 목록 — 웹(`SupportedDevices`)·앱 웹뷰(`GamesSupportedDevicesWebView`)가 **같은** 결과를 쓴다.
 *
 * 기기: `DeviceUtil.getSupportedDeviceSlugs`(표 `DEVICE_TYPE_TABLE`) → 상품·가격 필터.
 * 번들: `useValidBundles()`(통화·판매 가능 게임 필터 — `docs/bundle-region-policy.md`) → `pickSupportedBundles`.
 * 순서: 상품 API 순서 + 장갑 맨 앞(`orderSupportedDevices`).
 * 게임별 교체: `gameSlug` 가 `GAME_SUPPORTED_DEVICE_SWAPS` 에 있으면 그 번들 자리에 게임 전용 번들(판매 목록에 있을 때만).
 */
export const useSupportedDeviceProducts = (
  devices: DeviceType[],
  gameSlug?: string,
) => {
  const { data: products } = useProducts();
  const priced = usePricedFilter();
  const validBundles = useValidBundles();

  return useMemo(() => {
    const deviceSlugs = DeviceUtil.getSupportedDeviceSlugs(devices);
    const bundleSlugs = new Set(
      DeviceUtil.pickSupportedBundles(
        validBundles,
        products ?? [],
        deviceSlugs,
      ).map((b) => b.slug),
    );
    const wanted = new Set<string>([...deviceSlugs, ...bundleSlugs]);
    return DeviceUtil.applySupportedDeviceSwaps(
      DeviceUtil.orderSupportedDevices(
        priced((products ?? []).filter((p) => wanted.has(p.slug))),
      ),
      gameSlug ? GAME_SUPPORTED_DEVICE_SWAPS[gameSlug] : undefined,
      validBundles,
    );
  }, [devices, gameSlug, products, priced, validBundles]);
};
